
import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { enPathOf, langFromPath, localizePath } from '../../lib/lang';

// ── T274 · two FORMS, one switch ─────────────────────────────────────────────
// The dropdown is `absolute top-full` — it can only open DOWNWARDS. In the
// desktop header that is free: the trigger sits ~56px down a viewport with
// hundreds of pixels underneath it. Inside the mobile menu it is fatal. The
// panel lives at the bottom of a sticky header that, with the menu open,
// measures 1058px against an 860px viewport (hub's production reading at
// 719×860), so the trigger itself is already ~140px BELOW the fold and the
// popup it spawns lands at y 997–1103 — `elementFromPoint` at an option's
// centre returns null, because there is no point. The visitor cannot switch
// language at all: the owner's "cut off" (2026-08-02), and a P0.
//
// The fix is not "flip the popup upwards" — that only moves which viewport it
// escapes from. A popup that does not exist cannot open off-screen, so inside
// the menu the control becomes an INLINE SEGMENTED pair: both languages
// rendered in the flow, one tap to switch, no layer, no anchor, nothing to
// clip. The desktop header keeps the dropdown exactly as it was (it works
// there, and the card forbids touching it).
//
// The two forms share ONE `languages` array and ONE `handleLanguageChange` —
// the switch is not copied, only re-dressed. That is the point of the whole
// change: `enPathOf` → `localizePath` → `navigate` is byte-for-byte the chain
// it already was, so this card cannot alter WHERE a language click goes, only
// whether the visitor can reach the click.
export type LanguageSwitcherVariant = 'dropdown' | 'inline';

export interface LanguageSwitcherProps {
  /** `dropdown` (default) = the desktop header's popup, unchanged. `inline` =
   *  the mobile menu's segmented pair: always in the flow, never a layer. */
  variant?: LanguageSwitcherVariant;
}

export default function LanguageSwitcher({ variant = 'dropdown' }: LanguageSwitcherProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    // [HOTFIX 2026-07-28] flag emoji → text marks (design-hybrid §7; Kimi #19): flags
    // conflate language with nationality and render inconsistently across platforms.
    { code: 'en', name: 'English', flag: 'EN' },
    { code: 'zh', name: '中文', flag: '中' },
  ];

  // Language is driven by the URL, not just i18n state.
  const current = langFromPath(location.pathname);
  const currentLanguage = languages.find(lang => lang.code === current) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode: string) => {
    setIsOpen(false);
    if (langCode === current) return;
    const enPath = enPathOf(location.pathname);
    const target = localizePath(enPath, langCode as 'en' | 'zh');
    navigate(`${target}${location.search}${location.hash}`);
  };

  // ── the inline segmented form (mobile menu) ────────────────────────────────
  // Two buttons replacing two buttons: the dropdown already renders exactly
  // these two options, so this is the same target count at the same 44px floor
  // (I32) — the craft ledger must not grow a row. `aria-pressed` carries the
  // selection to assistive tech; the tick and the teal fill carry it visually,
  // reusing the drawer's own active language (`bg-teal-50 text-teal-600`) so a
  // selected language reads like a selected nav row. Zero new copy: the names
  // and marks are the same `languages` entries the dropdown shows.
  if (variant === 'inline') {
    return (
      <div className="flex items-stretch gap-2" data-lang-switcher="inline">
        {languages.map((lang) => {
          const selected = currentLanguage.code === lang.code;
          return (
            <button
              key={lang.code}
              type="button"
              lang={lang.code}
              aria-pressed={selected}
              onClick={() => handleLanguageChange(lang.code)}
              className={`flex min-h-[44px] flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                selected
                  ? 'border-teal-600 bg-teal-50 text-teal-600'
                  : 'border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="text-sm font-semibold">{lang.name}</span>
              {selected && <i className="ri-check-line" aria-hidden="true"></i>}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
      >
        <span className="text-lg">{currentLanguage.flag}</span>
        <span className="text-sm font-medium text-slate-700">{currentLanguage.name}</span>
        <i className={`ri-arrow-down-s-line text-slate-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-slate-200 py-2 min-w-[140px] z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors cursor-pointer ${
                currentLanguage.code === lang.code ? 'bg-slate-50' : ''
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="text-sm font-medium text-slate-700">{lang.name}</span>
              {currentLanguage.code === lang.code && (
                <i className="ri-check-line text-slate-900 ml-auto"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
